import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { api, apiErr, apiErrKind } from '@/lib/api';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const INACTIVITY_MS = 20 * 60 * 1000; // 20 min

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [errorKind, setErrorKind] = useState('');
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setUser(false);
  }, []);

  const checkInactivity = useCallback(() => {
    if (Date.now() - lastActivityRef.current > INACTIVITY_MS) logout();
  }, [logout]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/auth/me'); setUser(data); }
      catch { setUser(false); }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    ['click','keydown','touchstart','scroll','visibilitychange'].forEach(ev => window.addEventListener(ev, bump, { passive: true }));
    timerRef.current = setInterval(checkInactivity, 30000);
    return () => {
      ['click','keydown','touchstart','scroll','visibilitychange'].forEach(ev => window.removeEventListener(ev, bump));
      clearInterval(timerRef.current);
    };
  }, [user, checkInactivity]);

  const login = async (email, password) => {
    setError(''); setErrorKind('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data?.requires_2fa) {
        return { requires_2fa: true, challenge_token: data.challenge_token };
      }
      setUser(data);
      return { ok: true, needs_nip_setup: !!data.needs_nip_setup };
    } catch (e) {
      setError(apiErr(e)); setErrorKind(apiErrKind(e));
      return { ok: false };
    }
  };

  const verify2FA = async (challenge_token, code) => {
    try {
      const { data } = await api.post('/auth/2fa/verify', { challenge_token, code });
      setUser(data);
      return { ok: true, needs_nip_setup: !!data.needs_nip_setup };
    } catch (e) {
      return { ok: false, error: apiErr(e) };
    }
  };

  return (
    <AuthCtx.Provider value={{ user, login, logout, verify2FA, error, errorKind, setError }}>
      {children}
    </AuthCtx.Provider>
  );
}
